/* -------------------------- Design imports start -------------------------- */
import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material'

/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import Cookies from 'js-cookie'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import logo from '../../assets/logo_black.svg'
import Button from '../../components/Button'
import Page from '../../components/layout/Page'
import { PERMISSIONS } from '../../config/permissions'
import { useUserContext } from '../../utils/context'
import { fetchCurrentUser } from '../../api/userApi'
import { canView, rolesToPermissions } from '../../utils/functions'
import { useGuestToken } from '../../features/Auth/hooks/useAuthMutations'
import { isFeatureEnabled } from '../../config/featureFlags'
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function GuestSignInPage() {
  /* -------------------------- Non state data start -------------------------- */
  const { t } = useTranslation()
  const { setAccessToken } = useUserContext()
  const { token } = useParams<{ token: string }>()
  const history = useHistory()
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  
  // React Query hook for guest token
  const guestTokenMutation = useGuestToken()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [showPassword, setShowPassword] = useState(false)
  const [isTokenValidating, setIsTokenValidating] = useState(true)
  const [tokenError, setTokenError] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [password, setPassword] = useState<string>('')
  /* ----------------------------- Data states end ---------------------------- */

  const resolveGuestRedirectPath = useCallback(async (): Promise<string> => {
    try {
      const currentUser = await fetchCurrentUser(['roles'])
      if (!Array.isArray(currentUser.roles)) {
        return '/items'
      }

      const userPermissions = rolesToPermissions(currentUser.roles)
      const hasBOMAccess =
        canView(userPermissions, PERMISSIONS.ITEMS.BOM_BASE) ||
        canView(userPermissions, PERMISSIONS.ITEMS.BOM)

      if (isFeatureEnabled('bom') && hasBOMAccess) {
        return '/items/bom/overview'
      }

      return '/items'
    } catch {
      return '/items'
    }
  }, [])

  /* ------------------------------ Effects start ----------------------------- */
  // Validate token presence on load so we can show an error before any submit attempt
  useEffect(() => {
    setIsTokenValidating(true)
    if (!token || !uuidRegex.test(token)) {
      setTokenError(true)
      setIsTokenValidating(false)
      return
    }

    setTokenError(false)
    setIsTokenValidating(false)
  }, [token])

  useEffect(() => {
    const redirectAuthenticatedGuest = async (): Promise<void> => {
      if (isTokenValidating || guestTokenMutation.isPending || !Cookies.get('access')) {
        return
      }
      const redirectPath = await resolveGuestRedirectPath()
      history.push(redirectPath)
    }

    void redirectAuthenticatedGuest()
  }, [guestTokenMutation.isPending, history, isTokenValidating, resolveGuestRedirectPath])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  // some API functions like fetch, create, ...
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleSignIn = async (e: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    e.preventDefault()
    if ((tokenError || !token) && !isTokenValidating) {
      setTokenError(true)
      return
    }

    if (token && password) {
      try {
        const data = await guestTokenMutation.mutateAsync({ guestId: token, password })
        // Cookies are already set in guestTokenApi, just update context
        setAccessToken(data.access)
      } catch (error) {
        // Error is handled by React Query and logged in the hook
        // The error state is available via guestTokenMutation.isError
      }
    }
  }

  /**
   * Send login request on pressing enter when the password textfield is the active element.
   * @param e
   */
  const handleKeyDown = async (e: any): Promise<void> => {
    if (e.key === 'Enter') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      e.preventDefault()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      e.stopPropagation()
      await handleSignIn(e)
    }
  }

  const handleClickShowPassword = () => setShowPassword(show => !show)

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // datastructures that are used for rendering and don't need to be state
  // e.g. formFields of the Form component, columns of the antd table
  /* -------------------------- Render constants end -------------------------- */
  const translatedHint = t('content.auth.guestSignInHint')
  const guestSignInHint =
    translatedHint && translatedHint !== 'content.auth.guestSignInHint'
      ? translatedHint
      : 'Guest access: You were invited to view shared items. Use the provided password to continue.'

  /* ------------------------ Pre render actions start ------------------------ */
  // get guestId from url
  // if(!guestId) {
  //   const urlParams = new URLSearchParams(window.location.search)
  //   const token = urlParams.get("token")
  //   setGuestId(token ?? "invalid token")
  // }

  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  const isLoading = isTokenValidating || guestTokenMutation.isPending
  const signInError = guestTokenMutation.isError

  if (isLoading) return <CircularProgress />

  return (
    <Page title="">
      <Box width="100%" display="flex" flexDirection="column" alignItems="center">
        <img
          src={logo}
          alt="assemblean Logo"
          style={{
            width: '250px',
            height: 'auto',
            marginBottom: '50px',
          }}
        />
        <Grid container spacing={2} width="60%">
          {tokenError ? (
            <Grid item xs={12}>
              <Typography variant="h5" align="center">
                {t('content.auth.verificationError', 'Verification error')}
              </Typography>
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="h5" align="center">
                  {t('content.auth.signIn')}
                </Typography>
                <Typography variant="body2" align="center">
                  {guestSignInHint}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="password">{t('common:content.auth.password')}</InputLabel>
                  <OutlinedInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          id="togglePasswordVisibilityButton"
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleKeyDown}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label={t('common:content.auth.password')}
                  />
                  <FormHelperText sx={{ ml: '-1px' }} id="password-helper-text">
                    {t('common:content.auth.passwordHelperTextGuest')}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {signInError && (
                <Grid item xs={12}>
                  <Typography color="error" align="center">
                    {t('content.auth.signInError', 'Email or password is incorrect.')}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  style={{ color: 'whitesmoke', textTransform: 'none' }}
                  fullWidth
                  onClick={handleSignIn}
                  onKeyDown={handleKeyDown}
                  disabled={tokenError}
                >
                  {t('content.auth.signIn')}
                </Button>
              </Grid>
            </>
          )}
          {tokenError && (
            <Grid item xs={12}>
              <Typography variant="body1" align="center">
                {t(
                  'content.auth.verificationErrorText',
                  'The verification link is invalid or has expired.'
                )}
              </Typography>
            </Grid>
          )}
          {tokenError && (
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="info"
                fullWidth
                onClick={() => history.push('/signIn')}
                style={{ textTransform: 'none' }}
              >
                {t('content.auth.backToSignIn')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Page>
  )
}
